import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Store, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const EtsyCard = ({ config }) => {
  const [busy, setBusy] = useState(false);
  const [access, setAccess] = useState(null);
  const configured = config?.etsy_configured;
  useEffect(() => { api.get("/etsy/access").then((r) => setAccess(r.data)).catch(() => {}); }, []);
  const blocked = access && (!access.allowed || !configured) && access.message;
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/etsy/oauth/start");
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div data-testid="connect-etsy-card" className="rounded-2xl border glass p-6 sm:p-8 flex flex-col">
      <Store className="h-7 w-7 text-primary" />
      <h2 className="mt-4 text-2xl font-medium">Your real Etsy shop</h2>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
        Connect through Etsy's official OAuth. We ask only for listings (read & write), shop and transactions (read). Nothing changes until you approve.
      </p>
      <p className="mt-3 text-xs text-muted-foreground font-mono" data-testid="beta-shop-count">
        Founding beta: {config?.real_spots_assigned ?? 0} of {config?.max_real_shops ?? 5} real-shop spots assigned
      </p>
      <div className="mt-auto pt-6">
        {blocked ? (
          <p data-testid="real-access-message" data-access={access.access} className="flex items-start gap-2 rounded-2xl border border-[#E9C98B]/35 bg-[#E9C98B]/[0.07] px-4 py-3 text-sm text-[#F3DDAE]">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />{access.message}
          </p>
        ) : (
          <Button onClick={connect} disabled={!configured || busy} data-testid="connect-etsy-button"
            className={`w-full rounded-full h-auto min-h-11 whitespace-normal py-2.5 ${configured ? "btn-aurora" : "cursor-not-allowed border border-dashed border-white/25 bg-white/[0.06] text-[#ECE9F5] hover:bg-white/[0.06] disabled:opacity-100"}`}>
            {!configured && <Lock className="h-4 w-4 mr-1 shrink-0" />}
            {configured ? (busy ? "Opening Etsy…" : "Connect Etsy shop") : "Etsy connection available soon — try Demo Mode"}
          </Button>
        )}
      </div>
    </div>
  );
};

const DemoCard = () => {
  const { loadShop } = useAuth();
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/shop/demo");
      await loadShop();
      toast.success("Demo shop connected — 300 sample listings scored.");
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div data-testid="connect-demo-card" className="rounded-2xl border-2 border-[#E9C98B]/40 bg-[#E9C98B]/[0.06] p-6 sm:p-8 flex flex-col">
      <FlaskConical className="h-7 w-7 text-[#E9C98B]" />
      <h2 className="mt-4 text-2xl font-medium">Demo Mode</h2>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
        Explore the full loop with "Demo Vintage & POD Co.": 300 simulated listings, about 25 protected bestsellers and 75 days of simulated stats.
        Everything is labeled "Demo data" and is never presented as real.
      </p>
      <div className="mt-auto pt-6">
        <Button onClick={start} disabled={busy} data-testid="try-demo-mode-button" variant="outline" className="w-full rounded-full h-11 border-[#E9C98B]/45 bg-white/5 hover:bg-[#E9C98B]/10">
          {busy ? "Building your demo shop…" : "Try Demo Mode"}
        </Button>
      </div>
    </div>
  );
};

export const ConnectShop = () => {
  const { config, user } = useAuth();
  return (
    <div data-testid="connect-shop-screen" className="fade-up">
      <p className="eyebrow">Step 1</p>
      <h1 className="mt-3 text-4xl sm:text-6xl leading-[1.02]">Connect a shop to get your Health Report</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Connecting changes nothing on Etsy. You'll see a score for every listing first.</p>
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        <EtsyCard config={config} />
        {user?.role === "admin" && <DemoCard />}
      </div>
    </div>
  );
};
